import React, { useState } from 'react';
import { Calendar, RefreshCw, AlertTriangle, CheckCircle, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpiryCard = ({ data }) => {
  if (data.error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
        <div className="text-sm text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {data.error}
        </div>
      </div>
    );
  }

  const isExpiry = data.is_expiry_day;
  const isShifted = data.shifted_from_holiday;
  const borderColor = isExpiry ? 'border-amber-500/50' : 'border-border';
  const bgColor = isExpiry ? 'bg-amber-500/5' : 'bg-surface';

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-3`} data-testid={`expiry-card-${data.symbol?.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-white">{data.symbol}</span>
        <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-500/30">
          {data.expiry_type}
        </Badge>
      </div>

      <div className="text-lg font-mono font-bold text-white mb-1">
        {data.expiry_display}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="text-gray-400">
          YYMMDD: <span className="text-cyan-300 font-mono">{data.expiry_code_yymmdd}</span>
        </div>
        <div className="text-gray-400">
          Days: <span className={`font-mono font-bold ${data.days_to_expiry <= 1 ? 'text-amber-400' : 'text-white'}`}>
            {data.days_to_expiry}
          </span>
        </div>
      </div>

      {isExpiry && (
        <div className="mt-2 flex items-center gap-1 text-xs text-amber-400">
          <Clock className="w-3 h-3" />
          EXPIRY DAY
        </div>
      )}

      {isShifted && (
        <div className="mt-2 flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/10 rounded px-2 py-1">
          <AlertTriangle className="w-3 h-3" />
          Holiday-shifted from {data.original_date}
        </div>
      )}

      <div className="mt-2 text-[10px] text-gray-500">
        Sample: <span className="font-mono text-gray-400">{data.sample_symbol}</span>
      </div>
    </div>
  );
};

const HolidayRow = ({ holiday, isPast }) => (
  <tr className={`border-b border-white/5 ${isPast ? 'opacity-40' : ''}`}>
    <td className="py-1.5 px-2 text-xs font-mono text-gray-300">{holiday.date}</td>
    <td className="py-1.5 px-2 text-xs text-gray-400">{holiday.weekday?.slice(0, 3)}</td>
    <td className="py-1.5 px-2 text-xs text-white">{holiday.description}</td>
    <td className="py-1.5 px-2 text-center">
      {isPast ? (
        <span className="text-[10px] text-gray-600">Past</span>
      ) : (
        <CheckCircle className="w-3 h-3 text-green-400 inline" />
      )}
    </td>
  </tr>
);

const ExpiryCheckPanel = () => {
  const [expiryData, setExpiryData] = useState(null);
  const [holidayData, setHolidayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHolidays, setShowHolidays] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [expiryRes, holidayRes] = await Promise.all([
        fetch(`${API}/admin/expiry-check`, { headers }),
        fetch(`${API}/admin/holiday-calendar`, { headers }),
      ]);

      if (!expiryRes.ok) throw new Error(`Expiry check failed: HTTP ${expiryRes.status}`);
      if (!holidayRes.ok) throw new Error(`Holiday fetch failed: HTTP ${holidayRes.status}`);

      setExpiryData(await expiryRes.json());
      setHolidayData(await holidayRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="rounded-lg border border-border bg-surface p-4 mb-4" data-testid="expiry-check-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cyan-400" />
          Expiry Check & Holiday Calendar
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          data-testid="check-expiry-btn"
          className="text-xs h-7"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Checking...' : expiryData ? 'Refresh' : 'Check Expiry Dates'}
        </Button>
      </div>

      {error && (
        <div className="text-xs text-red-400 flex items-center gap-1 mb-3">
          <AlertTriangle className="w-3 h-3" /> {error}
        </div>
      )}

      {expiryData && (
        <>
          <div className="text-[10px] text-gray-500 mb-3">
            Checked: {expiryData.checked_at} | Today: {expiryData.today_display}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            {expiryData.expiries?.nifty && <ExpiryCard data={expiryData.expiries.nifty} />}
            {expiryData.expiries?.banknifty && <ExpiryCard data={expiryData.expiries.banknifty} />}
            {expiryData.expiries?.stock_futures && <ExpiryCard data={expiryData.expiries.stock_futures} />}
          </div>
        </>
      )}

      {holidayData && (
        <div>
          <button
            className="text-xs text-gray-400 hover:text-white flex items-center gap-1 mb-2 transition-colors"
            onClick={() => setShowHolidays(!showHolidays)}
            data-testid="toggle-holidays-btn"
          >
            <ArrowRight className={`w-3 h-3 transition-transform ${showHolidays ? 'rotate-90' : ''}`} />
            Holiday Calendar ({holidayData.total_holidays} holidays, {holidayData.total_special_days} special days)
          </button>

          {showHolidays && (
            <div className="bg-gray-900/50 rounded-lg border border-white/5 overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-gray-800/30">
                    <th className="py-2 px-2 text-[10px] font-medium text-gray-500 uppercase">Date</th>
                    <th className="py-2 px-2 text-[10px] font-medium text-gray-500 uppercase">Day</th>
                    <th className="py-2 px-2 text-[10px] font-medium text-gray-500 uppercase">Holiday</th>
                    <th className="py-2 px-2 text-[10px] font-medium text-gray-500 uppercase text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {holidayData.holidays?.map((h, i) => (
                    <HolidayRow key={i} holiday={h} isPast={h.date < todayStr} />
                  ))}
                </tbody>
              </table>

              {holidayData.special_trading_days?.length > 0 && (
                <div className="border-t border-white/10 p-2">
                  <div className="text-[10px] text-amber-400 font-semibold mb-1">Special Trading Days</div>
                  {holidayData.special_trading_days.map((s, i) => (
                    <div key={i} className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="font-mono">{s.date}</span>
                      <span className="text-gray-500">{s.weekday?.slice(0, 3)}</span>
                      <span>{s.description}</span>
                      {s.trading_hours && (
                        <Badge variant="outline" className="text-[9px] text-amber-400 border-amber-500/30">
                          {s.trading_hours}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!expiryData && !loading && (
        <div className="text-xs text-gray-500 text-center py-4">
          Click "Check Expiry Dates" to verify next expiry for NIFTY, BANKNIFTY & Stock Futures
        </div>
      )}
    </div>
  );
};

export default ExpiryCheckPanel;
